import clsx from 'clsx';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import LeadCaptureModalBody, {
  LeadCaptureModalBodyProps,
} from '@components/MarketingModal/LeadCaptureModalBody';
import { ModalHeaderProps } from '@components/MarketingModal/common/modalHeader';
import { OFFER } from '@components/MarketingModal/modals/constants';
import ModalArticle from '@components/ModalArticle';
import Spinner from '@components/Spinner';
import useAddSearchParam from '@hooks/useAddSearchParam';
import useArticlesBySlug from '@hooks/useArticlesBySlug';
import { Theme } from '@interfaces/global';

export type LeadCaptureModalWithSingleArticleProps = ModalHeaderProps & LeadCaptureModalBodyProps;

/**
 * Lead capture modal for a single article.
 * Growth calls this the Acquisition P6 Modal - Single Article.
 */
const LeadCaptureModalWithSingleArticle = ({
  articleSlugs,
  buttonText,
  customUrl,
  highlight,
  leadType,
  modalId,
  modalType,
  offerCode,
  offerLink,
  subtitle,
  title,
}: LeadCaptureModalWithSingleArticleProps) => {
  const { data, isLoading } = useArticlesBySlug({ articleSlugs });

  const parameterizedCustomUrl = useAddSearchParam(customUrl, OFFER, offerCode);
  const offerLinkWithCustomUrl = customUrl?.length > 0 ? parameterizedCustomUrl : offerLink;

  const utmParams = {
    utm_source: 'bc_modal',
    utm_campaign: modalType,
    utm_content: modalId,
  };

  return (
    <div className="flex w-full flex-col-reverse md:flex-row">
      <div className="w-full flex-1 bg-white">
        <div className="px-24 py-32 sm:pb-80 sm:pt-48">
          <div className="m-auto sm:w-[410px] md:w-[384px]">
            {highlight && (
              <span className="mb-8 font-suisse-intl-mono text-overline-sm text-primary-magenta sm:text-overline-md">
                {highlight}
              </span>
            )}
            <h2 className="my-0 font-suisse-works text-headline-sm sm:text-headline-lg">
              {ReactHtmlParser(title)}
            </h2>
            <p className="my-0 mb-24 font-suisse-intl text-description-lg sm:mb-32">
              {ReactHtmlParser(subtitle)}
            </p>
            <div className="w-full">
              <LeadCaptureModalBody
                buttonText={buttonText}
                customUrl={offerLinkWithCustomUrl}
                leadType={leadType}
                offerLink={offerLink}
                theme={Theme.Light}
                withOauth={false}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        className={clsx('flex w-full flex-1 py-32 sm:items-center sm:py-40', {
          'pr-24 sm:pr-88 md:pr-40': !isLoading,
        })}
      >
        {isLoading ? <Spinner /> : <ModalArticle {...data[0]} utmParams={utmParams} />}
      </div>
    </div>
  );
};

export default LeadCaptureModalWithSingleArticle;
